import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  KeyRound,
  Pencil,
  Plus,
  Users,
} from 'lucide-react';
import { api } from '../lib/api.js';
import { DAY_NAMES } from '../lib/constants.js';
import { DAY_END, DAY_START, HOUR_HEIGHT } from '../lib/calendar.js';
import {
  addDays,
  calendarStartMonday,
  entryHours,
  fmtDur,
  isoDate,
  parseIsoDate,
} from '../lib/time.js';
import { roleLabel } from '../lib/roles.js';
import { shapeWithChoices, sortOf } from '../lib/sessionState.js';
import { useSessionState } from '../lib/useSessionState.js';
import { countActiveFilters } from '../lib/listFilters.js';
import { validIso } from '../lib/dateInput.js';
import { ResetFiltersButton } from '../components/controls/ResetFiltersButton.jsx';
import {
  Avatar,
  Card,
  flashRowProps,
  Modal,
  Progress,
  ProjectLabel,
  Stats,
  Table,
} from '../components/ui.jsx';
import { TeamModal, UserModal } from '../components/AdminModals.jsx';
import {
  ReportFilters,
  ReportRangeFilter,
} from '../components/reporting/ReportFilters.jsx';
import {
  useReportData,
  useReportFilters,
} from '../components/reporting/useReportFilters.js';
import {
  EntryProjectLabel,
  entryAppearance,
} from '../components/CalendarEntry.jsx';
import { DonutChart } from '../components/reporting/ReportCharts.jsx';
import { buildSlices } from '../components/reporting/chartUtils.js';
import { buildMemberClientRows } from '../components/reporting/reportUtils.js';
import { sumByWorkType } from '../lib/workTypes.js';
import {
  DetailMeta,
  DetailShell,
  ListToolbar,
  PageHeader,
} from '../components/PageSkeleton.jsx';

const TEAM_FILTERS = { search: '', status: 'active' };
const TEAM_FILTERS_SHAPE = shapeWithChoices({
  status: ['active', 'inactive', 'all'],
});
const TEAM_SORT = { key: 'hours', direction: 'desc' };

// The member calendar's week, remembered together with the report range it
// was stepped from: a new range still jumps the calendar to that range.
function sanitizeMemberWeek(value, fallback) {
  return value &&
    validIso(value.week) &&
    validIso(value.from) &&
    validIso(value.to)
    ? { week: value.week, from: value.from, to: value.to }
    : fallback;
}

const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

const TEAM_TABLE_HEADERS = [
  { label: 'Member', sortKey: 'member' },
  { label: 'Job title', sortKey: 'jobTitle' },
  { label: 'Team', sortKey: 'team' },
  { label: 'Role', sortKey: 'role' },
  { label: 'Clients', sortKey: 'clients' },
  { label: 'Projects', sortKey: 'projects' },
  { label: 'Hours in period', sortKey: 'hours' },
  { label: 'MDs in period', sortKey: 'mds' },
  { label: 'Absence', sortKey: 'absence' },
];
const SORT_SHAPE = sortOf(TEAM_TABLE_HEADERS.map((header) => header.sortKey));

const EMPTY_PERIOD_ROW = {
  hours: 0,
  absenceHours: 0,
  clientCount: 0,
  clientNames: [],
  projectCount: 0,
};

function layoutEntries(entries) {
  const sorted = [...entries].sort((a, b) => a.start - b.start);
  const result = new Map();
  let i = 0;
  while (i < sorted.length) {
    let maxEnd = sorted[i].end;
    let j = i + 1;
    while (j < sorted.length && sorted[j].start < maxEnd) {
      maxEnd = Math.max(maxEnd, sorted[j].end);
      j += 1;
    }
    const group = sorted.slice(i, j);
    const colEnds = [];
    for (const entry of group) {
      let col = colEnds.findIndex((end) => end <= entry.start);
      if (col === -1) col = colEnds.length;
      colEnds[col] = entry.end;
      result.set(entry.id, { col, cols: 0 });
    }
    for (const entry of group)
      result.set(entry.id, { ...result.get(entry.id), cols: colEnds.length });
    i = j;
  }
  return result;
}

// The list and the member detail are separate components on purpose: the list
// owns a `/reports/data` subscription, and now that the date range is shared
// app-wide, keeping it mounted behind an open detail would re-run the whole
// unscoped team report on every filter change made inside that detail.
export function Team({ data, reload, openUser, setOpenUser }) {
  // Everything the list view remembers lives here, not in `TeamList`, and in
  // the tab session (sessionState.js), so coming back from a member detail or
  // another page restores the search, status, sort and the checkbox filter
  // selections you left on. `useReportFilters` itself is cheap — only
  // `TeamList` subscribes to `/reports/data`.
  const [listFilters, setListFilters] = useSessionState(
    'team.filters',
    TEAM_FILTERS,
    TEAM_FILTERS_SHAPE,
  );
  const [sort, setSort] = useSessionState('team.sort', TEAM_SORT, SORT_SHAPE);
  const search = listFilters.search;
  const statusFilter = listFilters.status;
  const setSearch = (value) =>
    setListFilters((current) => ({ ...current, search: value }));
  const setStatusFilter = (value) =>
    setListFilters((current) => ({ ...current, status: value }));
  // This view is per team, so a team manager's roster, filters and KPI cards
  // all come from the teams they manage — even though /bootstrap hands them a
  // wider set (their own row, plus the people of their managed projects when
  // they are a project manager too, which Reports needs).
  const rosterData = useMemo(() => teamRoster(data), [data]);
  const filters = useReportFilters(rosterData, {
    scope: data.canManageTeam ? {} : { teamIds: data.managedTeamIds },
    persistKey: 'team',
  });

  if (openUser)
    // A member detail is entitlement-based rather than roster-based: Reports
    // sends a project manager straight here for somebody outside their teams,
    // which is why it reads the full bootstrap set.
    return (
      <TeamMemberDetail
        userId={openUser}
        data={data}
        reload={reload}
        onBack={() => setOpenUser(null)}
      />
    );
  return (
    <TeamList
      data={rosterData}
      reload={reload}
      setOpenUser={setOpenUser}
      filters={filters}
      search={search}
      setSearch={setSearch}
      statusFilter={statusFilter}
      setStatusFilter={setStatusFilter}
      onResetList={() => setListFilters(TEAM_FILTERS)}
      sort={sort}
      setSort={setSort}
    />
  );
}

function TeamList({
  data,
  reload,
  setOpenUser,
  filters,
  search,
  setSearch,
  statusFilter,
  setStatusFilter,
  onResetList,
  sort,
  setSort,
}) {
  const [showUser, setShowUser] = useState(false);
  const [showTeams, setShowTeams] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [flashToken, setFlashToken] = useState(0);
  const { report, loading } = useReportData(filters.query);
  // Team managers reach this view for visibility only — creating members and
  // teams stays with admins (canManageTeam).
  const canManage = data.canManageTeam;
  const managedTeamNames = teamNames(data, data.managedTeamIds);

  // One row per person for the period — hours plus how many clients and
  // projects they touched. Everyone the roster shows but the report does not
  // (nothing logged) reads as an empty period rather than a missing row.
  const periodByUserId = new Map(
    report.people.map((row) => [row.user.id, row]),
  );
  const periodRow = (userId) => periodByUserId.get(userId) || EMPTY_PERIOD_ROW;
  // Client/project/work-type selections narrow the roster to people who
  // actually logged matching time; a date range alone keeps everyone visible.
  const crossFilterActive =
    filters.clientIds.length > 0 ||
    filters.projectIds.length > 0 ||
    filters.billing !== 'all';
  const visibleUsers = data.users
    .filter((user) => {
      if (statusFilter === 'active' && !user.active) return false;
      if (statusFilter === 'inactive' && user.active) return false;
      if (filters.teamIds.length && !filters.teamIds.includes(user.teamId))
        return false;
      if (filters.userIds.length && !filters.userIds.includes(user.id))
        return false;
      if (crossFilterActive && !periodByUserId.has(user.id)) return false;
      const haystack =
        `${user.name} ${user.email} ${user.jobTitle} ${user.teamName}`.toLowerCase();
      return haystack.includes(search.toLowerCase());
    })
    .sort((a, b) => {
      const left = teamSortValue(a, sort.key, periodRow(a.id));
      const right = teamSortValue(b, sort.key, periodRow(b.id));
      const result =
        typeof left === 'number' && typeof right === 'number'
          ? left - right
          : collator.compare(String(left), String(right));
      return sort.direction === 'asc' ? result : -result;
    });

  function sortBy(key) {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' },
    );
    setFlashToken((n) => n + 1);
  }

  return (
    <div>
      <PageHeader
        title="Team"
        subtitle={
          canManage
            ? `${data.users.length} visible members across ${data.teams.length} teams · ${filters.from} to ${filters.to}${loading ? ' · Loading...' : ''}`
            : `${managedTeamNames || 'Your team'} · ${data.users.length} members · ${filters.from} to ${filters.to}${loading ? ' · Loading...' : ''}`
        }
        actions={
          canManage ? (
            <>
              <button className="secondary" onClick={() => setShowTeams(true)}>
                <Users size={14} /> Manage teams
              </button>
              <button className="primary" onClick={() => setShowUser(true)}>
                <Plus size={14} /> Add member
              </button>
            </>
          ) : null
        }
      />
      <Stats
        cards={[
          [
            'People in period',
            report.summary.userCount,
            `${filters.from} to ${filters.to}`,
          ],
          [
            'Hours in period',
            fmtDur(report.summary.totalHours),
            unpaidNote(
              report.summary.unpaidAbsenceHours,
              'work + paid absence',
            ),
          ],
          [
            'MDs in period',
            `${(report.summary.totalHours / 8).toFixed(2)} MD`,
            '8 hours per MD',
          ],
          [
            'Client work',
            `${report.summary.billablePct.toFixed(0)}%`,
            fmtDur(report.summary.billableHours),
          ],
          [
            'Clients · Projects',
            `${report.summary.clientCount} · ${report.summary.projectCount}`,
            'with logged time in period',
          ],
        ]}
      />
      <ReportFilters
        data={data}
        isMyReport={false}
        workTypeHours={report.summary.workTypeHours}
        {...filters}
      />
      <ListToolbar
        search={search}
        onSearch={setSearch}
        placeholder="Search members"
        filters={
          <div className="toolbar-filters">
            <div className="segmented inline" aria-label="Member status">
              {[
                ['active', 'Active'],
                ['inactive', 'Inactive'],
                ['all', 'All'],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={statusFilter === key ? 'active' : ''}
                  onClick={() => setStatusFilter(key)}
                >
                  {label}
                </button>
              ))}
            </div>
            <ResetFiltersButton
              count={countActiveFilters(
                { search, status: statusFilter },
                TEAM_FILTERS,
              )}
              onReset={onResetList}
            />
          </div>
        }
      />
      <Table headers={TEAM_TABLE_HEADERS} sort={sort} onSort={sortBy}>
        {visibleUsers.map((user) => {
          const period = periodRow(user.id);
          const clientNames = (period.clientNames || []).join(', ');
          const { key, className } = flashRowProps(
            user.id,
            flashToken,
            'clickable',
          );
          return (
            <tr
              key={key}
              className={className}
              onClick={() => setOpenUser(user.id)}
            >
              <td>
                <div className="person">
                  <Avatar user={user} /> <strong>{user.name}</strong>
                </div>
              </td>
              <td>{user.jobTitle || '-'}</td>
              <td>{user.teamName || '-'}</td>
              <td>{roleLabel(user.role)}</td>
              <td>
                <div className="clients-cell">
                  <strong>{period.clientCount}</strong>
                  {clientNames && (
                    <span className="truncate" data-tooltip={clientNames}>
                      {clientNames}
                    </span>
                  )}
                </div>
              </td>
              <td className="strong">{period.projectCount}</td>
              <td className="strong">{fmtDur(period.hours)}</td>
              <td>{(period.hours / 8).toFixed(2)}</td>
              <td>{period.absenceHours ? fmtDur(period.absenceHours) : '-'}</td>
            </tr>
          );
        })}
      </Table>
      {showUser && (
        <UserModal
          data={data}
          onClose={() => setShowUser(false)}
          onCreated={async (password) => {
            setShowUser(false);
            setGeneratedPassword(password);
            await reload();
          }}
        />
      )}
      {generatedPassword && (
        <GeneratedPasswordModal
          password={generatedPassword}
          onClose={() => setGeneratedPassword('')}
        />
      )}
      {showTeams && (
        <TeamModal
          data={data}
          reload={reload}
          onClose={() => setShowTeams(false)}
        />
      )}
    </div>
  );
}

// Admins keep the whole roster; everybody else who reaches this view is a
// team manager, and sees exactly the teams they manage.
function teamRoster(data) {
  if (data.canManageTeam) return data;
  const managed = new Set(data.managedTeamIds || []);
  return {
    ...data,
    teams: data.teams.filter((team) => managed.has(team.id)),
    users: data.users.filter((user) => managed.has(user.teamId)),
  };
}

// Team ids (a caller's managed teams, or a member's) as readable names; ids
// outside `data.teams` are dropped, so a scoped bootstrap never leaks a name.
function teamNames(data, teamIds) {
  return (teamIds || [])
    .map((id) => data.teams.find((team) => team.id === id)?.name)
    .filter(Boolean)
    .join(', ');
}

// Hours are work plus paid absence; unpaid absence is not counted, so a stat
// that shows hours says how much of it it left out.
function unpaidNote(unpaidAbsenceHours, fallback) {
  return unpaidAbsenceHours
    ? `${fmtDur(unpaidAbsenceHours)} unpaid absence not counted`
    : fallback;
}

function teamSortValue(user, key, period) {
  if (key === 'member') return user.name;
  if (key === 'jobTitle') return user.jobTitle || '';
  if (key === 'team') return user.teamName || '';
  if (key === 'role') return roleLabel(user.role);
  if (key === 'clients') return period.clientCount;
  if (key === 'projects') return period.projectCount;
  if (key === 'hours') return period.hours;
  if (key === 'mds') return period.hours / 8;
  if (key === 'absence') return period.absenceHours;
  return '';
}

function TeamMemberDetail({ userId, data, reload, onBack }) {
  const user = data.users.find((item) => item.id === userId);
  const canManage = data.canManageTeam;
  const managesTeams = teamNames(data, user.managedTeamIds);
  const filters = useReportFilters(data, { scope: { userId } });
  const { report, loading } = useReportData(filters.query);
  const [editing, setEditing] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const total = report.summary.totalHours;
  const work = report.summary.workHours;
  const billable = report.summary.billableHours;
  // Absence has its own rows by project, but it is no project anyone works on.
  const projectRows = report.projects.filter((row) => !row.project.absence);
  const { clients: clientRows, nonClient } = buildMemberClientRows(
    report.clients,
    projectRows,
  );
  // Clients the period touched but never billed — internal work only.
  const internalClients = Math.max(
    0,
    report.summary.clientCount - clientRows.length,
  );
  const projectChart = buildSlices(
    projectRows,
    ({ project }) => `${project.code} ${project.name}`,
    ({ project }) => project.id,
  );
  const periodLabel = `${filters.from} to ${filters.to}`;

  async function resetPassword() {
    const result = await api(`/users/${user.id}/reset-password`, {
      method: 'POST',
    });
    setGeneratedPassword(result.initPassword);
  }

  return (
    <>
      <DetailShell
        backLabel="All team members"
        onBack={onBack}
        marker={<Avatar user={user} />}
        title={user.name}
        subtitle={`${user.jobTitle || 'Team member'} · ${user.teamName || 'No team'} · ${roleLabel(user.role)}`}
        meta={
          <DetailMeta
            items={[
              { label: 'Email', value: user.email },
              {
                label: 'Status',
                value: (
                  <>
                    <span
                      className={`member-status-dot${user.active ? '' : ' inactive'}`}
                    />
                    {user.active ? 'Active' : 'Inactive'}
                  </>
                ),
              },
              // Set per team in Manage teams, so it stays read-only here.
              { label: 'Team manager of', value: managesTeams },
            ]}
          />
        }
        actions={
          canManage ? (
            <>
              <button className="secondary" onClick={resetPassword}>
                <KeyRound size={14} /> Reset password
              </button>
              <button className="secondary" onClick={() => setEditing(true)}>
                <Pencil size={14} /> Edit member
              </button>
            </>
          ) : null
        }
        filters={<ReportRangeFilter {...filters} />}
        stats={
          <Stats
            cards={[
              [
                'Total hours',
                fmtDur(total),
                `${(total / 8).toFixed(2)} MD · ${unpaidNote(report.summary.unpaidAbsenceHours, periodLabel)}${loading ? ' · Loading...' : ''}`,
              ],
              ['Client work hours', fmtDur(billable), periodLabel],
              [
                'Client work',
                `${(work ? (billable / work) * 100 : 0).toFixed(0)}%`,
                periodLabel,
              ],
              [
                'Clients',
                clientRows.length,
                internalClients
                  ? `with client work · +${internalClients} internal`
                  : 'with client work in period',
              ],
              [
                'Active projects',
                projectRows.length,
                'with time logged in period',
              ],
            ]}
          />
        }
        left={
          <>
            <MemberWeekCalendar
              user={user}
              data={data}
              rangeFrom={filters.from}
              rangeTo={filters.to}
            />
            <Card
              title={`Project breakdown · ${periodLabel}`}
              className="member-breakdown-card"
            >
              <Table headers={['Project', 'Client', 'Hours', 'Share']}>
                {projectRows.map(({ project, hours }) => (
                  <tr key={project.id}>
                    <td>
                      <ProjectLabel project={project} />
                    </td>
                    <td>{project.clientName}</td>
                    <td className="strong">{fmtDur(hours)}</td>
                    <td>
                      <Progress pct={(hours / Math.max(1, total)) * 100} />
                    </td>
                  </tr>
                ))}
              </Table>
            </Card>
          </>
        }
        right={
          <>
            <MemberClientsCard
              rows={clientRows}
              nonClient={nonClient}
              total={total}
            />
            <Card title="Projects in period" className="member-projects-card">
              <div className="pie-card">
                <DonutChart
                  title="Projects in period"
                  slices={projectChart.slices}
                  total={projectChart.total}
                />
              </div>
            </Card>
          </>
        }
      />
      {editing && (
        <UserModal
          data={data}
          user={user}
          onClose={() => setEditing(false)}
          onSaved={async () => {
            setEditing(false);
            await reload();
          }}
        />
      )}
      {generatedPassword && (
        <GeneratedPasswordModal
          password={generatedPassword}
          onClose={() => setGeneratedPassword('')}
        />
      )}
    </>
  );
}

// Who the member worked for in the period, each client opening to the
// projects behind it. Non-client work is a single line at the bottom rather
// than a client of its own — it is the part of the period that bills nobody.
function MemberClientsCard({ rows, nonClient, total }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const share = (hours) => (total ? (hours / total) * 100 : 0);

  function toggle(clientId) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  }

  return (
    <section className="card member-clients-card">
      <div className="card-head">
        <h2>Clients in period</h2>
        <span className="count-pill">
          {rows.length} {rows.length === 1 ? 'client' : 'clients'}
        </span>
      </div>
      {!rows.length && !nonClient.hours && (
        <p className="hint">No time logged in the selected period</p>
      )}
      {rows.map(({ client, hours, projects }) => {
        const open = expanded.has(client.id);
        return (
          <div key={client.id} className="client-period-row">
            <button
              type="button"
              className="client-period-main"
              aria-expanded={open}
              onClick={() => toggle(client.id)}
            >
              {open ? (
                <ChevronDown size={16} aria-hidden="true" />
              ) : (
                <ChevronRight size={16} aria-hidden="true" />
              )}
              <span className="client-period-name">
                <strong className="truncate">{client.name}</strong>
                <span>
                  {projects.length}{' '}
                  {projects.length === 1 ? 'project' : 'projects'}
                </span>
              </span>
              <span className="client-period-hours">{fmtDur(hours)}</span>
              <span className="client-period-pct">
                {share(hours).toFixed(0)}%
              </span>
            </button>
            <div className="client-period-bar">
              <Progress pct={share(hours)} />
            </div>
            {open && Boolean(projects.length) && (
              <div className="client-period-projects">
                {projects.map(({ project, hours: projectHours }) => (
                  <div key={project.id} className="client-period-project">
                    <ProjectLabel project={project} muted />
                    <span className="client-period-hours">
                      {fmtDur(projectHours)}
                    </span>
                    <span className="client-period-pct">
                      {share(projectHours).toFixed(0)}%
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {Boolean(nonClient.hours) && (
        <div className="client-period-foot">
          <span className="client-period-name">
            <strong>Non-client work</strong>
            <span className="truncate">{nonClient.clientNames.join(', ')}</span>
          </span>
          <span className="client-period-hours">{fmtDur(nonClient.hours)}</span>
          <span className="client-period-pct">
            {share(nonClient.hours).toFixed(0)}%
          </span>
        </div>
      )}
    </section>
  );
}

function MemberWeekCalendar({ user, data, rangeFrom, rangeTo }) {
  // Stepping weeks is remembered per member until the report range changes.
  const [storedWeek, setStoredWeek] = useSessionState(
    `team.member.${user.id}.week`,
    null,
    sanitizeMemberWeek,
  );
  const monday =
    storedWeek && storedWeek.from === rangeFrom && storedWeek.to === rangeTo
      ? parseIsoDate(storedWeek.week)
      : calendarStartMonday(rangeFrom, rangeTo);
  const setMonday = (step) =>
    setStoredWeek({
      week: isoDate(step(monday)),
      from: rangeFrom,
      to: rangeTo,
    });
  const [weekEntries, setWeekEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const gridRef = useRef(null);
  const weekDates = Array.from({ length: 7 }, (_, index) =>
    addDays(monday, index),
  );
  const from = isoDate(monday);
  const to = isoDate(addDays(monday, 6));

  // A range change jumps the calendar to the week it implies (the remembered
  // week belongs to the old range); the stepper walks week by week from there.
  const { total, unpaidAbsence } = sumByWorkType(weekEntries, [
    ...data.projects,
    ...data.allProjects,
  ]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    api(`/users/${user.id}/time-entries?from=${from}&to=${to}`)
      .then((result) => {
        if (active) setWeekEntries(result.entries || []);
      })
      .catch((err) => {
        if (active) {
          setWeekEntries([]);
          setError(err.message);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [user.id, from, to]);

  useEffect(() => {
    if (gridRef.current) gridRef.current.scrollTop = 6 * HOUR_HEIGHT;
  }, [from]);

  return (
    <section className="member-calendar-section">
      <div className="member-calendar-head">
        <div>
          <h2>{user.name} calendar</h2>
          <p>
            {from} - {to} ·{' '}
            {loading
              ? 'Loading...'
              : `${fmtDur(total)}${unpaidAbsence ? ` · ${fmtDur(unpaidAbsence)} unpaid absence` : ''}`}
          </p>
        </div>
        <div
          className="segmented inline calendar-stepper"
          aria-label="Change week"
        >
          <button
            onClick={() => setMonday((current) => addDays(current, -7))}
            data-tooltip="Previous week"
            aria-label="Previous week"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            onClick={() => setMonday((current) => addDays(current, 7))}
            data-tooltip="Next week"
            aria-label="Next week"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
      <div className="member-calendar-grid calendar-grid" ref={gridRef}>
        <div className="time-axis" />
        {weekDates.map((date, day) => (
          <div
            key={day}
            className={`day-head ${day >= 5 ? 'weekend' : ''}`}
            style={{ gridColumn: day + 2 }}
          >
            <strong>{DAY_NAMES[day]}</strong>
            <span>{date.getDate()}</span>
          </div>
        ))}
        <div className="time-column">
          {Array.from({ length: DAY_END - DAY_START }, (_, index) => (
            <div
              key={`h${index}`}
              className="time-label"
              style={{ top: index * HOUR_HEIGHT - 7 }}
            >{`${DAY_START + index}:00`}</div>
          ))}
        </div>
        {weekDates.map((date, day) => {
          const workDate = isoDate(date);
          const dayEntries = weekEntries.filter(
            (entry) => entry.workDate === workDate,
          );
          const layout = layoutEntries(dayEntries);
          return (
            <div
              key={day}
              className={`day-column ${day >= 5 ? 'weekend' : ''}`}
              style={{ gridColumn: day + 2 }}
            >
              {day < 5 && (
                <>
                  <div
                    className="non-work-block"
                    style={{ top: 0, height: (8 - DAY_START) * HOUR_HEIGHT }}
                  />
                  <div
                    className="non-work-block"
                    style={{
                      top: (18 - DAY_START) * HOUR_HEIGHT,
                      height: (DAY_END - 18) * HOUR_HEIGHT,
                    }}
                  />
                </>
              )}
              {Array.from({ length: DAY_END - DAY_START + 1 }, (_, index) => (
                <div
                  key={index}
                  className="hour-line"
                  style={{ top: index * HOUR_HEIGHT }}
                />
              ))}
              {dayEntries.map((entry) => {
                const { col = 0, cols = 1 } = layout.get(entry.id) || {};
                return (
                  <ReadOnlyCalendarEntry
                    key={entry.id}
                    entry={entry}
                    data={data}
                    col={col}
                    cols={cols}
                  />
                );
              })}
            </div>
          );
        })}
        {!loading && !error && !weekEntries.length && (
          <div className="member-calendar-empty">No entries in this week</div>
        )}
        {!loading && error && (
          <div className="member-calendar-empty">{error}</div>
        )}
      </div>
    </section>
  );
}

function ReadOnlyCalendarEntry({ entry, data, col, cols }) {
  const project = data.allProjects.find(
    (item) => item.id === entry.projectId,
  ) ||
    data.projects.find((item) => item.id === entry.projectId) || {
      code: entry.projectCode,
      name: entry.projectName,
      color: entry.color,
    };
  const appearance = entryAppearance(project, entry.color);
  return (
    <div
      className={`cal-entry readonly-cal-entry ${appearance.className}`.trim()}
      style={{
        top: (entry.start - DAY_START) * HOUR_HEIGHT,
        height: Math.max(
          HOUR_HEIGHT * 0.25,
          (entry.end - entry.start) * HOUR_HEIGHT,
        ),
        left: cols > 1 ? `${(col / cols) * 100}%` : 0,
        width: cols > 1 ? `calc(${(1 / cols) * 100}% - 3px)` : undefined,
        ...appearance.colors,
      }}
    >
      <div className="cal-entry-body">
        <div className="cal-entry-top">
          {entry.description ? (
            <span className="cal-entry-desc">{entry.description}</span>
          ) : (
            <EntryProjectLabel project={project} />
          )}
          <span className="cal-entry-dur">{fmtDur(entryHours(entry))}</span>
        </div>
        {entry.description && <EntryProjectLabel project={project} />}
      </div>
    </div>
  );
}

function GeneratedPasswordModal({ password, onClose }) {
  return (
    <Modal title="Generated password" onClose={onClose}>
      <div className="form-grid">
        <label>
          Password
          <input
            className="generated-password"
            value={password}
            readOnly
            onFocus={(event) => event.target.select()}
          />
        </label>
        <p className="hint">
          Share this password with the user and ask them to change it after
          login.
        </p>
        <div className="modal-actions">
          <span />
          <button className="primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}
